/**
 * Client-side crypto — SPEC.md §1 and §2.
 *
 * This file is one half of a byte-for-byte contract with `cli/peepshow.py`.
 * `cli/tests/vectors.json` is the referee: `crypto.test.ts` replays it, and
 * nothing in here may change without regenerating and re-passing those vectors.
 *
 * The master key exists only as a Uint8Array in this tab's memory. It is never
 * persisted, never sent, never logged.
 */

import { scrypt as scryptJs } from 'scrypt-js';

import { b64decode, utf8 } from './base64';

export { b64decode, b64encode, fromUtf8, utf8 } from './base64';

export const SCRYPT_N = 1 << 15;
export const SCRYPT_R = 8;
export const SCRYPT_P = 1;
export const SCRYPT_DKLEN = 32;

export const INFO_META = 'peepshow/meta/v1';
export const INFO_WRAP = 'peepshow/wrap/v1';

const PS1_MAGIC = [0x50, 0x53, 0x31]; // "PS1"
const PS1_VERSION = 0x01;
const PS1_HEADER_LEN = 4;
const NONCE_LEN = 12;
const TAG_LEN = 16;

export const SALT_LEN = 16;
export const CONTENT_KEY_LEN = 16; // AES-128 — the HLS spec's requirement.
export const CONTENT_IV_LEN = 16;

/**
 * scrypt(N=2^15, r=8, p=1) -> 32-byte master key.
 *
 * WebCrypto has no scrypt, so this is scrypt-js. N is capped at 2^15 (~32 MB)
 * precisely so a browser survives it; raising N later is a re-key migration,
 * not a config tweak.
 */
export async function deriveMasterKey(
  passphrase: string,
  salt: Uint8Array,
  onProgress?: (fraction: number) => void,
): Promise<Uint8Array> {
  if (salt.length !== SALT_LEN) {
    throw new Error(`salt must be ${SALT_LEN} bytes, got ${salt.length}`);
  }
  // NFKC so a passphrase typed on macOS and on Linux normalises to the same
  // bytes — combining-accent differences would otherwise break unlock in a way
  // that looks exactly like a forgotten passphrase.
  const pw = utf8(passphrase.normalize('NFKC'));
  const out = await scryptJs(
    pw,
    salt,
    SCRYPT_N,
    SCRYPT_R,
    SCRYPT_P,
    SCRYPT_DKLEN,
    onProgress
      ? (p) => {
          onProgress(p);
          return false;
        }
      : undefined,
  );
  return new Uint8Array(out);
}

/**
 * HKDF-SHA256 with an empty salt (RFC 5869), matching the CLI.
 *
 * An empty salt means a zero-filled salt of hash length — which is exactly
 * what the CLI's HMAC-with-a-zeroed-key extract step computes.
 */
export async function hkdf(key: Uint8Array, info: string, length = 32): Promise<Uint8Array> {
  const ikm = await crypto.subtle.importKey('raw', toBuffer(key), 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new ArrayBuffer(0), info: toBuffer(utf8(info)) },
    ikm,
    length * 8,
  );
  return new Uint8Array(bits);
}

export interface VaultKeys {
  /** Metadata key — opens item payloads and manifest blobs. */
  meta: CryptoKey;
  /** Wrapping key — opens per-video content keys. */
  wrap: CryptoKey;
  metaRaw: Uint8Array;
  wrapRaw: Uint8Array;
}

/** Split the master key into the two subkeys the whole app runs on. */
export async function deriveVaultKeys(master: Uint8Array): Promise<VaultKeys> {
  const [metaRaw, wrapRaw] = await Promise.all([hkdf(master, INFO_META), hkdf(master, INFO_WRAP)]);
  const [meta, wrap] = await Promise.all([importAesKey(metaRaw), importAesKey(wrapRaw)]);
  return { meta, wrap, metaRaw, wrapRaw };
}

/** Import raw bytes as a non-extractable AES-GCM key. */
export function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', toBuffer(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

/**
 * Encrypt to the PS1 envelope: "PS1" || 0x01 || nonce(12) || ct||tag.
 *
 * `nonce` exists for the conformance vectors. Production callers must let it
 * default to random: a reused nonce under one key is a total GCM break.
 */
export async function seal(
  key: CryptoKey,
  plaintext: Uint8Array,
  nonce?: Uint8Array,
): Promise<Uint8Array> {
  const iv = nonce ?? crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  if (iv.length !== NONCE_LEN) throw new Error(`nonce must be ${NONCE_LEN} bytes`);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: toBuffer(iv), tagLength: 128 },
      key,
      toBuffer(plaintext),
    ),
  );
  const out = new Uint8Array(PS1_HEADER_LEN + NONCE_LEN + ct.length);
  out.set(PS1_MAGIC, 0);
  out[3] = PS1_VERSION;
  out.set(iv, PS1_HEADER_LEN);
  out.set(ct, PS1_HEADER_LEN + NONCE_LEN);
  return out;
}

/**
 * Decrypt a PS1 envelope.
 *
 * Every failure — bad magic, wrong version, wrong key, tampered tag — throws.
 * A throw means "wrong passphrase or corrupt blob"; there is no partial output
 * to fall back on, by design.
 */
export async function unseal(key: CryptoKey, blob: Uint8Array): Promise<Uint8Array> {
  if (blob.length < PS1_HEADER_LEN + NONCE_LEN + TAG_LEN) {
    throw new Error('PS1 blob is too short');
  }
  if (blob[0] !== PS1_MAGIC[0] || blob[1] !== PS1_MAGIC[1] || blob[2] !== PS1_MAGIC[2]) {
    throw new Error('not a PS1 blob');
  }
  if (blob[3] !== PS1_VERSION) throw new Error(`unsupported PS1 version ${blob[3]}`);
  const iv = blob.subarray(PS1_HEADER_LEN, PS1_HEADER_LEN + NONCE_LEN);
  const body = blob.subarray(PS1_HEADER_LEN + NONCE_LEN);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBuffer(iv), tagLength: 128 },
    key,
    toBuffer(body),
  );
  return new Uint8Array(plain);
}

export async function sealJson(key: CryptoKey, value: unknown): Promise<Uint8Array> {
  return seal(key, utf8(JSON.stringify(value)));
}

export async function unsealJson<T>(key: CryptoKey, blob: Uint8Array): Promise<T> {
  return JSON.parse(new TextDecoder().decode(await unseal(key, blob))) as T;
}

export interface ContentKey {
  key: Uint8Array; // 16 bytes, AES-128
  iv: Uint8Array; // 16 bytes
}

/** Unwrap a per-video content key: PS1(wrapKey, key16 || iv16). */
export async function unwrapContentKey(wrapKey: CryptoKey, wrappedB64: string): Promise<ContentKey> {
  const plain = await unseal(wrapKey, b64decode(wrappedB64));
  if (plain.length !== CONTENT_KEY_LEN + CONTENT_IV_LEN) {
    throw new Error(
      `wrapped content key must be ${CONTENT_KEY_LEN + CONTENT_IV_LEN} bytes, got ${plain.length}`,
    );
  }
  return { key: plain.slice(0, CONTENT_KEY_LEN), iv: plain.slice(CONTENT_KEY_LEN) };
}

export function randomSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_LEN));
}

/** Overwrite key material in place. Best-effort — JS makes no real promises. */
export function zero(bytes: Uint8Array): void {
  bytes.fill(0);
}

/**
 * Uint8Array -> ArrayBuffer covering exactly this view.
 *
 * Subarrays share a larger buffer; handing `.buffer` straight to WebCrypto
 * would silently include the neighbours.
 */
function toBuffer(bytes: Uint8Array): ArrayBuffer {
  if (bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength) {
    return bytes.buffer as ArrayBuffer;
  }
  return bytes.slice().buffer as ArrayBuffer;
}
